import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";

import Checkbox from "../components/Checkbox";
import EllipsisLoader from "../components/EllipsisLoader";
import TextInput from "../components/TextInput";
import { ApiContext, NavigationContext, UserContext } from "../contexts";
import useWindowSize from "../hooks/useWindowSize";
import { IS_DEV } from "../resources/constants";
import { RoutePath } from "../types";
import { scrollToTop } from "../util";

const SignUpScreen = observer(() => {
  const apiContext = useContext(ApiContext);
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  const windowSize = useWindowSize();

  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordConfirmError, setPasswordConfirmError] = useState("");

  useEffect(() => {
    scrollToTop();
    userContext.resetSignUpFields();
    navContext.resetOnNavigateBack();
    navContext.resetNextBtn();
    navContext.setNextBtnText("Continue");
    navContext.setNextButtonEnabled(false);
    navContext.setLoginButtonVisible(false);
    navContext.setRestartButtonVisible(true);
    navContext.setBackButtonVisible(true);
    navContext.setSubtotalVisible(false);
    navContext.setNextBtnOnClick(submitSignUpForm);
  }, []);

  useEffect(() => {
    // Need to re-run this whenever passwordConfirm changes otherwise
    // the most up-to-date value won't be used
    navContext.setNextBtnOnClick(submitSignUpForm);
  }, [passwordConfirm]);

  useEffect(() => {
    if (windowSize.width > 768) {
      navContext.setNextButtonVisible(false);
    } else {
      navContext.setNextButtonVisible(true);
    }
  }, [windowSize.width]);

  useEffect(() => {
    if (apiContext.isLoading) {
      navContext.setNextButtonEnabled(false);
      setIsFormValid(false);
      return;
    }
    if (IS_DEV) {
      navContext.setNextButtonEnabled(true);
      setIsFormValid(true);
      return;
    }
    if (
      userContext.firstName.trim() !== "" &&
      userContext.lastName.trim() !== "" &&
      userContext.email.trim() !== "" &&
      /\S+@\S+\.\S+/.test(userContext.email) && // Email regex
      userContext.phone.trim() !== "" &&
      userContext.password !== "" &&
      userContext.password === passwordConfirm
    ) {
      navContext.setNextButtonEnabled(true);
      setIsFormValid(true);
    } else {
      navContext.setNextButtonEnabled(false);
      setIsFormValid(false);
    }
  }, [
    userContext.firstName,
    userContext.lastName,
    userContext.phone,
    userContext.email,
    userContext.password,
    passwordConfirm,
    apiContext.isLoading,
  ]);

  const submitSignUpForm = async () => {
    setFirstNameError("");
    setLastNameError("");
    setPhoneError("");
    setEmailError("");
    setPasswordError("");
    setPasswordConfirmError("");
    try {
      const result = await apiContext.registerUser({
        name: userContext.firstName,
        surname: userContext.lastName,
        email: userContext.email,
        phone: userContext.phone,
        opt_in: userContext.optIn,
        password: userContext.password,
        password_confirmation: passwordConfirm,
      });

      if (result.errors) {
        if (result.errors.name) {
          setFirstNameError(result.errors.name[0]);
        }
        if (result.errors.surname) {
          setLastNameError(result.errors.surname[0]);
        }
        if (result.errors.phone) {
          setPhoneError(result.errors.phone[0]);
        }
        if (result.errors.email) {
          setEmailError(result.errors.email[0]);
        }
        if (result.errors.password) {
          setPasswordError(result.errors.password[0]);
        }
        if (result.errors.password_confirmation) {
          setPasswordConfirmError(result.errors.password_confirmation[0]);
        }
      } else if (result.error) {
        alert(result.error);
      } else {
        if(!IS_DEV){
          try {
            // @ts-ignore
            window.gtag("event", "SUCCEEDED", {
              "event_category": "ENQUIRY", 
              "event_label": "pyht-sign-up"
            });
            // @ts-ignore
            window.ttq.track("SubmitForm", {
              "contents": [
                {
                  "content_id": window.location.href,
                  "content_type": "Form Submitted Successfully",
                  "content_name": "pyht-sign-up"
                }
              ]
            });
          } catch (e) {
            console.error(e);
          }
        }
        userContext.setIsLoggedIn(true);
        userContext.setIsNewUser(true);
        navContext.goNext();
      }
    } catch (e: any) {
      alert(e.message);
    }
  };

  const onClickNext = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await submitSignUpForm();
  };

  const onClickLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navContext.goTo(RoutePath.LOGIN);
  };

  return (
    <div className={"screen screen-sign-up container"}>
      <h2 className="h1">
        Great Job, You&apos;re Halfway There. <br />
        Create A My Domaine Account To Continue.
      </h2>
      <p className={"subtitle"}>
        Create a My Domaine account and continue to personalise your new home.
        <br />
        See your interior come to life by selecting your must-have inclusions,
        lifestyle options and interior colours.
        <br />
        Once you’ve completed your selections, you’ll receive an obligation free
        estimate with upfront pricing!
      </p>

      <form className={"sign-up-form"}>
        <div className={"form-group"}>
          <div className={"input-wrapper"}>
            <TextInput
              id={"name"}
              placeholder={"First Name*"}
              value={userContext.firstName}
              onChange={(value) => userContext.setFirstName(value.trim())}
              onScreenKeyboard={userContext.isKioskMode}
              className={firstNameError !== "" ? "error" : ""}
              disabled={apiContext.isLoading}
            />
            {firstNameError !== "" && (
              <p className={"error-msg"}>{firstNameError}</p>
            )}
          </div>
          <div className={"input-wrapper"}>
            <TextInput
              id={"surname"}
              placeholder={"Last Name*"}
              value={userContext.lastName}
              onChange={(value) => userContext.setLastName(value.trim())}
              onScreenKeyboard={userContext.isKioskMode}
              className={lastNameError !== "" ? "error" : ""}
              disabled={apiContext.isLoading}
            />
            {lastNameError !== "" && (
              <p className={"error-msg"}>{lastNameError}</p>
            )}
          </div>
        </div>

        <div className={"form-group"}>
          <div className={"input-wrapper"}>
            <TextInput
              id={"phone"}
              placeholder={"Phone*"}
              value={userContext.phone}
              onChange={(value) => userContext.setPhone(value.trim())}
              onScreenKeyboard={userContext.isKioskMode}
              className={phoneError !== "" ? "error" : ""}
              disabled={apiContext.isLoading}
            />
            {phoneError !== "" && <p className={"error-msg"}>{phoneError}</p>}
          </div>
          <div className={"input-wrapper"}>
            <TextInput
              id={"email"}
              placeholder={"Email*"}
              value={userContext.email}
              onChange={(value) => userContext.setEmail(value.trim())}
              onScreenKeyboard={userContext.isKioskMode}
              className={emailError !== "" ? "error" : ""}
              disabled={apiContext.isLoading}
            />
            {emailError !== "" && <p className={"error-msg"}>{emailError}</p>}
          </div>
        </div>

        <div className={"form-group"}>
          <div className={"input-wrapper"}>
            <TextInput
              id={"password"}
              placeholder={"Password*"}
              value={userContext.password}
              onChange={(value) => userContext.setPassword(value)}
              onScreenKeyboard={userContext.isKioskMode}
              type={"password"}
              className={passwordError !== "" ? "error" : ""}
              disabled={apiContext.isLoading}
            />
            {passwordError !== "" && (
              <p className={"error-msg"}>{passwordError}</p>
            )}
          </div>
          <div className={"input-wrapper"}>
            <TextInput
              id={"password_confirmation"}
              placeholder={"Confirm Password*"}
              value={passwordConfirm}
              type={"password"}
              onChange={(value) => setPasswordConfirm(value)}
              onScreenKeyboard={userContext.isKioskMode}
              className={passwordConfirmError !== "" ? "error" : ""}
              disabled={apiContext.isLoading}
            />
            {passwordConfirmError !== "" && (
              <p className={"error-msg"}>{passwordConfirmError}</p>
            )}
          </div>
        </div>
        <div className={"form-group form-footer"}>
          <Checkbox
            checked={userContext.optIn}
            onChange={(value) => userContext.setOptIn(value)}
            disabled={apiContext.isLoading}
          >
            <>
              I would like to subscribe to receive updates on the latest promotions, events and product news.
            </>
          </Checkbox>
          <div>
            <button
              disabled={!isFormValid}
              className={"btn submit-btn"}
              onClick={onClickNext}
            >
              Continue to Personalise Your Home
            </button>
            <p className={"required-msg"}>
              <span className={"red"}>*</span> Required fields
            </p>
          </div>
        </div>
      </form>
      <div className={"login-link"}>
        <span>Please see our <a href="/nsw/privacy" target="_blank">Privacy Policy</a> for more information.</span>
        <a href={"#"} onClick={onClickLogin}>
          Already have an account? Login now
        </a>
      </div>
      {apiContext.isLoading && (
        <div className={"fullscreen-loader"}>
          <EllipsisLoader />
        </div>
      )}
    </div>
  );
});

export default SignUpScreen;
